/**
 * ChatHistoryService
 *
 * Service for managing chat history stored in the Ada canister.
 * Provides CRUD operations for chat conversations with ICP persistence.
 *
 * Sprint 28: Chat History Feature
 */

// ======================
// Type Definitions
// ======================

export interface ChatHistoryMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: number;
}

export interface ChatHistoryEntry {
    id: string;
    title: string;
    messages: ChatHistoryMessage[];
    preview: string;
    created_at: number;  // bigint from canister, converted to number
    updated_at: number;
    user_principal: string;
}

export interface CreateChatHistoryRequest {
    title?: string;
    messages: ChatHistoryMessage[];
}

export interface UpdateChatHistoryRequest {
    id: string;
    title?: string;
    messages?: ChatHistoryMessage[];
}

export interface ChatHistoryListResponse {
    entries: ChatHistoryEntry[];
    total_count: number;
}

// ======================
// ChatHistoryService Class
// ======================

export class ChatHistoryService {
    private static instance: ChatHistoryService | null = null;
    private canisterClient: any = null;

    /**
     * Singleton pattern
     */
    public static getInstance(): ChatHistoryService {
        if (!ChatHistoryService.instance) {
            ChatHistoryService.instance = new ChatHistoryService();
        }
        return ChatHistoryService.instance;
    }

    private constructor() {
        console.log('ChatHistoryService: Initialized');
    }

    /**
     * Get the canister client (lazy initialization)
     */
    private getClient(): any {
        if (!this.canisterClient) {
            this.canisterClient = (window as any).canisterClient;
        }
        if (!this.canisterClient) {
            throw new Error('CanisterClient not available');
        }
        return this.canisterClient;
    }

    /**
     * Convert bigint timestamps to numbers
     */
    private convertEntry(entry: any): ChatHistoryEntry {
        return {
            ...entry,
            created_at: Number(entry.created_at),
            updated_at: Number(entry.updated_at),
            user_principal: entry.user_principal?.toString() || '',
            messages: entry.messages.map((m: any) => ({
                ...m,
                timestamp: Number(m.timestamp)
            }))
        };
    }

    /**
     * Create a new chat history entry
     */
    async create(request: CreateChatHistoryRequest): Promise<ChatHistoryEntry> {
        console.log('ChatHistoryService: Creating new entry', request);

        const client = this.getClient();

        // Convert messages to canister format
        const canisterRequest = {
            title: request.title ? [request.title] : [],
            messages: request.messages.map(m => ({
                id: m.id,
                role: m.role,
                content: m.content,
                timestamp: BigInt(m.timestamp)
            }))
        };

        const result = await client.callCanister('ada', 'create_chat_history_entry', [canisterRequest]);

        if ('Ok' in result) {
            const entry = this.convertEntry(result.Ok);
            console.log('ChatHistoryService: Created entry', entry.id);
            return entry;
        } else {
            throw new Error(result.Err || 'Failed to create chat history entry');
        }
    }

    /**
     * Get a chat history entry by ID
     */
    async get(chatId: string): Promise<ChatHistoryEntry | null> {
        console.log('ChatHistoryService: Getting entry', chatId);

        const client = this.getClient();
        const result = await client.callCanister('ada', 'get_chat_history_entry', [chatId]);

        // Handle different response formats:
        // - Direct optional: [entry] or []
        // - Proxy format: { data: entry, success: true } or { data: [entry], success: true }
        let entry = null;

        if (result) {
            if (result.data) {
                // Proxy format with data wrapper
                entry = Array.isArray(result.data) ? result.data[0] : result.data;
            } else if (Array.isArray(result) && result.length > 0) {
                // Direct optional array format
                entry = result[0];
            } else if (result.id) {
                // Direct entry object
                entry = result;
            }
        }

        if (entry && entry.id) {
            return this.convertEntry(entry);
        }
        return null;
    }

    /**
     * Update an existing chat history entry
     */
    async update(request: UpdateChatHistoryRequest): Promise<ChatHistoryEntry> {
        console.log('ChatHistoryService: Updating entry', request.id);

        const client = this.getClient();

        // Convert to canister format with optional fields
        const canisterRequest: any = {
            id: request.id,
            title: request.title ? [request.title] : [],
            messages: request.messages ? [request.messages.map(m => ({
                id: m.id,
                role: m.role,
                content: m.content,
                timestamp: BigInt(m.timestamp)
            }))] : []
        };

        const result = await client.callCanister('ada', 'update_chat_history_entry', [canisterRequest]);

        if ('Ok' in result) {
            const entry = this.convertEntry(result.Ok);
            console.log('ChatHistoryService: Updated entry', entry.id);
            return entry;
        } else {
            throw new Error(result.Err || 'Failed to update chat history entry');
        }
    }

    /**
     * Delete a chat history entry
     */
    async delete(chatId: string): Promise<boolean> {
        console.log('ChatHistoryService: Deleting entry', chatId);

        const client = this.getClient();
        const result = await client.callCanister('ada', 'delete_chat_history_entry', [chatId]);

        if ('Ok' in result) {
            console.log('ChatHistoryService: Deleted entry', chatId);
            return true;
        } else {
            throw new Error(result.Err || 'Failed to delete chat history entry');
        }
    }

    /**
     * List chat history entries with pagination
     */
    async list(limit: number = 20, offset: number = 0): Promise<ChatHistoryListResponse> {
        console.log('ChatHistoryService: Listing entries', { limit, offset });

        const client = this.getClient();
        const result = await client.callCanister('ada', 'list_chat_history', [BigInt(limit), BigInt(offset)]);

        // Handle both response formats:
        // - Direct value: { success: true, data: { entries: [], total_count: N } }
        // - Result type: { entries: [], total_count: N }
        const data = result.data || result;

        return {
            entries: (data.entries || []).map((e: any) => this.convertEntry(e)),
            total_count: Number(data.total_count || 0)
        };
    }

    /**
     * Search chat history entries
     */
    async search(query: string, limit: number = 20): Promise<ChatHistoryEntry[]> {
        console.log('ChatHistoryService: Searching entries', { query, limit });

        const client = this.getClient();
        const result = await client.callCanister('ada', 'search_chat_history_entries', [query, BigInt(limit)]);

        // Handle both response formats
        const data = result.data || result;
        const entries = Array.isArray(data) ? data : (data.entries || []);

        return entries.map((e: any) => this.convertEntry(e));
    }

    /**
     * Save or update a conversation (convenience method)
     * Creates new entry if no ID, updates if ID exists
     */
    async saveConversation(
        messages: ChatHistoryMessage[],
        existingId?: string,
        title?: string
    ): Promise<ChatHistoryEntry> {
        if (existingId) {
            return this.update({
                id: existingId,
                title,
                messages
            });
        } else {
            return this.create({
                title,
                messages
            });
        }
    }

    /**
     * Generate a unique message ID
     */
    generateMessageId(): string {
        return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Format a timestamp for display
     */
    formatTimestamp(timestamp: number): string {
        const date = new Date(timestamp / 1_000_000); // Convert from nanoseconds
        const now = new Date();
        const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return `Today, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
        } else if (diffDays === 1) {
            return `Yesterday, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
        } else if (diffDays < 7) {
            return date.toLocaleDateString([], { weekday: 'long', hour: '2-digit', minute: '2-digit' });
        } else {
            return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
        }
    }
}

// ======================
// Singleton Export
// ======================

export const chatHistoryService = ChatHistoryService.getInstance();

// Attach to window for global access
declare global {
    interface Window {
        chatHistoryService: ChatHistoryService;
    }
}

if (typeof window !== 'undefined') {
    window.chatHistoryService = chatHistoryService;
}

console.log('ChatHistoryService: Module loaded');
